import type { Tool } from "@modelcontextprotocol/sdk/types.js";
import {
  CopilotMcpConnectorCreateError,
  createCopilotMcpConnectorInputSchema,
  type CopilotMcpConnectorApi,
} from "../api/copilot-mcp-connector-api.js";

export { createCopilotMcpConnectorInputSchema };

export const createCopilotMcpConnectorTool: Tool = {
  name: "create_copilot_mcp_connector",
  description:
    "EXPERIMENTAL: preview or create a NEW Copilot Studio MCP custom connector using the observed native oauth2pkcewithprm provider. Preview is local and default; confirmCreate=true sends one bounded creation POST under existing same-account silent Studio management authentication. Supply an explicit environment, an existing solutionId, and a caller-chosen stable referenceId for inspection/recovery \u2014 referenceId is NOT proven idempotent. No caller client ID, secret, scope list or alternate provider is accepted. Does not create a connection, complete consent, grant permissions, modify an existing connector, bind an agent, publish or execute; creation may have licensing/capacity implications but does not purchase a license. HTTP 201 is acceptance only, with independent readback and runtime verification still required. After an ambiguous outcome, inspect existing connectors before another create \u2014 never retry automatically. This observed Studio route is not a documented public API.",
  annotations: {
    readOnlyHint: false,
    destructiveHint: false,
    idempotentHint: false,
    openWorldHint: true,
  },
  inputSchema: {
    type: "object",
    properties: {
      environment: {
        type: "string",
        pattern:
          "^(?:[Dd][Ee][Ff][Aa][Uu][Ll][Tt]-)?[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$",
        description: "Exact verified environment GUID, preserving Default- prefix.",
      },
      displayName: { type: "string", minLength: 1, maxLength: 100 },
      description: { type: "string", maxLength: 2000 },
      resourceUrl: {
        type: "string",
        maxLength: 2048,
        description:
          "Exact canonical HTTPS MCP server resource URL with public-host syntax, no credentials, query, fragment, explicit port or percent encoding. Not fetched by preview.",
      },
      solutionId: {
        type: "string",
        pattern: "^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$",
        description:
          "Existing solution GUID previously verified in the target environment; this tool does not create or look up solutions.",
      },
      referenceId: {
        type: "string",
        pattern: "^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$",
        description:
          "Caller-generated GUID kept for recovery inspection, not a proven idempotency key. Do not reuse it to automatically replay a create.",
      },
      confirmCreate: { type: "boolean", default: false },
    },
    required: [
      "environment",
      "displayName",
      "description",
      "resourceUrl",
      "solutionId",
      "referenceId",
    ],
    additionalProperties: false,
  },
};

export interface CopilotMcpConnectorToolContext {
  connectorApi: CopilotMcpConnectorApi | null;
}

export async function handleCreateCopilotMcpConnector(
  context: CopilotMcpConnectorToolContext,
  input: unknown,
) {
  const validation = createCopilotMcpConnectorInputSchema.safeParse(input);
  if (!validation.success) throw new CopilotMcpConnectorCreateError("invalid-input");
  const parsed = validation.data;
  if (parsed.confirmCreate !== true)
    return {
      text: "Preview only: no authentication, discovery or tenant request was made. Confirmation creates one new MCP custom connector; licensing/capacity requirements can apply. No connection, consent, permissions, agent binding, publication or evaluation is requested. Verify the solution and check for an existing connector first; referenceId does not guarantee idempotency.",
      structured: {
        connectorCreation: {
          experimental: true,
          status: "preview",
          environment: parsed.environment,
          displayName: parsed.displayName,
          description: parsed.description,
          resourceUrl: parsed.resourceUrl,
          solutionId: parsed.solutionId,
          referenceId: parsed.referenceId,
          provider: "oauth2pkcewithprm",
          redirectMode: "globalPerConnector",
          operationId: "InvokeServer",
          tenantWritePlanned: "Create one new connector only",
          independentReadbackVerified: false,
        },
      },
    };
  if (!context.connectorApi) throw new CopilotMcpConnectorCreateError("unavailable");
  const result = await context.connectorApi.createConnector(parsed);
  return {
    text: result.responseIdentityMatched
      ? "Connector creation accepted (HTTP 201); response identity matched. Independent stored-metadata readback, consent and runtime behavior are not verified. Inspect the connector before any further create."
      : "Connector creation accepted (HTTP 201), but response identity is unconfirmed and no connector ID is returned. Inspect existing connectors before another create; do not retry automatically.",
    structured: { connectorCreation: result },
  };
}
